import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { uploadImageToStorage } from "./ads.server";
import { displayNameKey, validateDisplayName } from "./display-name";

const UpdateProfileInput = z.object({
  displayName: z.string().trim().min(2).max(40),
  /** Privat telefonnummer – visas aldrig publikt. */
  phone: z.string().trim().max(30).optional(),
  /** Ny avatar som base64. Utelämnas om avataren inte ändrats. */
  avatarBase64: z.string().min(1).optional(),
  avatarMimeType: z.string().min(1).max(80).optional(),
  /** Originalbilden (komprimerad men obeskuren) som base64. */
  avatarOriginalBase64: z.string().min(1).optional(),
  avatarOriginalMimeType: z.string().min(1).max(80).optional(),
  /** Beskärningsläget som användes, så att justering kan återupptas. */
  avatarCrop: z
    .object({ x: z.number(), y: z.number(), zoom: z.number(), rotation: z.number() })
    .optional(),
  /** true = ta bort nuvarande avatar. */
  removeAvatar: z.boolean().optional(),
  /** Hämta profilbilden från användarens Google-konto (kräver aktivt val). */
  googleAvatarUrl: z.string().url().max(1000).optional(),
});

/** Bara Googles egna bildvärdar accepteras. */
function isGooglePhotoUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return (
      url.startsWith("https://") &&
      (host.endsWith("googleusercontent.com") || host.endsWith("google.com"))
    );
  } catch {
    return false;
  }
}

/** Laddar ner Google-bilden och gör om den till base64 för vår lagring. */
async function fetchGooglePhoto(url: string): Promise<{ base64: string; mimeType: string }> {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Kunde inte hämta profilbilden från Google.");
  const mimeType = res.headers.get("content-type") ?? "image/jpeg";
  if (!/^image\//i.test(mimeType)) throw new Error("Google-bilden gick inte att läsa.");
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.byteLength > 8 * 1024 * 1024) throw new Error("Google-bilden är för stor.");
  let binary = "";
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return { base64: btoa(binary), mimeType: mimeType.split(";")[0]! };
}


/** Hämtar den inloggades profil och skapar en tom profil om den saknas. */
export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [{ data }, { data: contact }] = await Promise.all([
      context.supabase
        .from("profiles")
        .select("display_name, avatar_url, avatar_original_url, avatar_crop")
        .eq("user_id", context.userId)
        .maybeSingle(),
      context.supabase
        .from("profile_contacts")
        .select("phone")
        .eq("user_id", context.userId)
        .maybeSingle(),
    ]);

    const names = await import("@/lib/display-names.server");
    const nameChange = await names.getNameChangeStatus(context.userId).catch(() => null);

    return {
      displayName: data?.display_name ?? "",
      phone: contact?.phone ?? "",
      avatarUrl: data?.avatar_url ?? null,
      avatarOriginalUrl: data?.avatar_original_url ?? null,
      avatarCrop: (data?.avatar_crop ?? null) as {
        x: number;
        y: number;
        zoom: number;
        rotation: number;
      } | null,
      email: (context.claims?.email as string | undefined) ?? "",
      /** Regler för namnbyte: kvarvarande byten, aktiva annonser m.m. */
      nameChange,
    };
  });


/** Släpper den tillfälliga namnreservationen, t.ex. när användaren lämnar profilsidan. */
export const releaseMyDisplayNameReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const reservations = await import("@/lib/display-names.server");
    await reservations.releaseReservation(context.userId).catch(() => undefined);
    return { ok: true };
  });

/** Kollar om ett visningsnamn är ledigt – förväxlingsbara namn räknas som upptagna. */
export const checkDisplayName = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ displayName: z.string().max(80), reserve: z.boolean().optional() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const formatError = validateDisplayName(data.displayName);
    if (formatError) return { available: false, reason: formatError };

    const key = displayNameKey(data.displayName);
    const { data: taken } = await context.supabase
      .from("profiles")
      .select("user_id")
      .eq("display_name_key", key)
      .neq("user_id", context.userId)
      .maybeSingle();

    if (taken)
      return {
        available: false,
        reason:
          "Namnet är upptaget, eller för likt ett befintligt namn. Prova något som skiljer sig mer.",
      };

    const reservations = await import("@/lib/display-names.server");
    await reservations.purgeExpiredReservations();
    if (await reservations.isReservedByOther(key, context.userId))
      return {
        available: false,
        reason: "Någon annan håller just nu på att registrera det här namnet. Välj ett annat.",
      };

    // Gamla namn ligger i karantän så att ingen annan kan ta över en identitet.
    if (await reservations.isHeldByOther(key, context.userId))
      return {
        available: false,
        reason:
          "Namnet har nyligen använts av någon annan och är spärrat en tid. Välj ett annat namn.",
      };


    if (data.reserve !== false) {
      try {
        await reservations.reserveDisplayName(key, context.userId);
      } catch {
        // Reservationen är en bekvämlighet – den får aldrig blockera kontrollen.
      }
    }

    return { available: true, reason: null };
  });


/** Sparar visningsnamn och avatar, och synkar namnet till alla användarens annonser. */
export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => UpdateProfileInput.parse(input))
  .handler(async ({ data, context }) => {
    const formatError = validateDisplayName(data.displayName);
    if (formatError) throw new Error(formatError);
    const nameKey = displayNameKey(data.displayName);

    const { data: currentProfile } = await context.supabase
      .from("profiles")
      .select("display_name, display_name_key")
      .eq("user_id", context.userId)
      .maybeSingle();
    const previousName = currentProfile?.display_name ?? "";
    const previousKey = currentProfile?.display_name_key ?? "";
    const isNameChange = previousKey !== "" && previousKey !== nameKey;

    const reservations = await import("@/lib/display-names.server");

    if (isNameChange) {
      const status = await reservations.getNameChangeStatus(context.userId);
      if (!status.canChange) throw new Error(status.reason ?? "Du kan inte byta visningsnamn nu.");
    }

    const { data: taken } = await context.supabase
      .from("profiles")
      .select("user_id")
      .eq("display_name_key", nameKey)
      .neq("user_id", context.userId)
      .maybeSingle();
    if (taken)
      throw new Error(
        "Visningsnamnet är upptaget, eller för likt ett befintligt namn. Välj ett annat.",
      );

    if (await reservations.isReservedByOther(nameKey, context.userId))
      throw new Error("Någon annan håller just nu på att registrera det här namnet. Välj ett annat.");

    if (await reservations.isHeldByOther(nameKey, context.userId))
      throw new Error(
        "Namnet har nyligen använts av någon annan och är spärrat en tid. Välj ett annat namn.",
      );




    let avatarUrl: string | null | undefined;
    let avatarOriginalUrl: string | null | undefined;
    let avatarCrop: Record<string, number> | null | undefined;
    if (data.removeAvatar) {
      avatarUrl = null;
      avatarOriginalUrl = null;
      avatarCrop = null;
    } else if (data.googleAvatarUrl) {
      if (!isGooglePhotoUrl(data.googleAvatarUrl))
        throw new Error("Bilden kunde inte hämtas från Google.");
      const photo = await fetchGooglePhoto(data.googleAvatarUrl);
      avatarUrl = await uploadImageToStorage(photo.base64, photo.mimeType);
      // Samma bild som original, så att den går att justera efteråt.
      avatarOriginalUrl = avatarUrl;
      avatarCrop = null;
    } else if (data.avatarBase64) {
      if (!/^image\//i.test(data.avatarMimeType ?? ""))
        throw new Error("Endast bildfiler tillåts som profilbild.");
      avatarUrl = await uploadImageToStorage(data.avatarBase64, data.avatarMimeType!);
      if (data.avatarOriginalBase64 && /^image\//i.test(data.avatarOriginalMimeType ?? "")) {
        avatarOriginalUrl = await uploadImageToStorage(
          data.avatarOriginalBase64,
          data.avatarOriginalMimeType!,
        );
      }
      avatarCrop = data.avatarCrop ?? null;
    }

    const { error } = await context.supabase.from("profiles").upsert(
      {
        user_id: context.userId,
        display_name: data.displayName,
        display_name_key: nameKey,
        ...(avatarUrl === undefined ? {} : { avatar_url: avatarUrl }),
        ...(avatarOriginalUrl === undefined ? {} : { avatar_original_url: avatarOriginalUrl }),
        ...(avatarCrop === undefined ? {} : { avatar_crop: avatarCrop }),
      },
      { onConflict: "user_id" },
    );
    if (error)
      throw new Error(
        error.code === "23505"
          ? "Visningsnamnet är upptaget, eller för likt ett befintligt namn. Välj ett annat."
          : `Kunde inte spara profilen: ${error.message}`,
      );

    if (data.phone !== undefined) {
      const { error: phoneError } = await context.supabase
        .from("profile_contacts")
        .upsert({ user_id: context.userId, phone: data.phone }, { onConflict: "user_id" });
      if (phoneError) throw new Error(`Kunde inte spara telefonnumret: ${phoneError.message}`);
    }

    // Namnet är nu vårt på riktigt – släpp den tillfälliga reservationen.
    await reservations.releaseReservation(context.userId).catch(() => undefined);

    // Spara bytet: gamla namnet hamnar i karantän och motparter ser "hette tidigare".
    if (isNameChange) {
      await reservations
        .recordNameChange(context.userId, previousName, previousKey, data.displayName, nameKey)
        .catch(() => undefined);
    }

    // Visningsnamnet hör till kontot – håll alla annonser i synk.
    await context.supabase
      .from("ads")
      .update({ contact_name: data.displayName })
      .eq("user_id", context.userId);

    return { ok: true, nameChanged: isNameChange };
  });

